/**
 * File Helper Utilities
 * Common file manipulation and formatting functions
 */

import { v4 as uuidv4 } from 'uuid';
import { getDocumentReviewStatusConfig } from '../components/Helpers/documentReviewStatusConfig';

/**
 * Convert a File object to a data URL
 * @param {File} file - File to convert
 * @returns {Promise<string>} Data URL string
 */
export const fileToDataUrl = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
        reader.readAsDataURL(file);
    });
};

/**
 * Format bytes to human-readable file size
 * @param {number|string} bytes - Size in bytes
 * @returns {string} Formatted file size (e.g., "1.5 MB")
 */
export const formatFileSize = (bytes) => {
    const n = Number(bytes);
    if (!Number.isFinite(n) || n <= 0) return '0 Bytes';
    
    const k = 1024;
    const units = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(n) / Math.log(k));
    
    return `${Math.round(n / Math.pow(k, i))} ${units[i]}`;
};

/**
 * Validate file against constraints
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @param {number} options.maxSizeMB - Maximum file size in MB
 * @param {Array<string>} options.allowedTypes - Allowed file extensions
 * @param {Array<string>} options.existingFileNames - Existing file names to check duplicates
 * @returns {Object} Validation result { valid: boolean, error?: string }
 */
export const validateFile = (file, options = {}) => {
    const { 
        maxSizeMB = 10, 
        allowedTypes = [], 
        existingFileNames = [] 
    } = options;
    
    // Validate file size
    const maxSizeBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxSizeBytes) {
        return {
            valid: false,
            error: `File "${file.name}" exceeds maximum size of ${maxSizeMB} MB`
        };
    }
    
    // Validate file type
    if (allowedTypes.length > 0) {
        const extension = file.name.split('.').pop()?.toLowerCase();
        if (!extension || !allowedTypes.includes(extension)) {
            return {
                valid: false,
                error: `File type ".${extension}" is not allowed. Allowed: ${allowedTypes.join(', ')}`
            };
        }
    }
    
    // Check for duplicates
    if (existingFileNames.includes(file.name)) {
        return {
            valid: false,
            error: `File "${file.name}" already exists`
        };
    }
    
    return { valid: true };
};

/**
 * Generate a unique temporary ID for pending uploads
 * @returns {string} Unique ID
 */
export const generateTempId = () => {
    return uuidv4();
};

/**
 * Get accept attribute value for file input
 * @param {Array<string>} allowedTypes - Array of file extensions
 * @returns {string} Accept attribute value
 */
export const getAcceptAttribute = (allowedTypes = []) => {
    return allowedTypes.map(type => `.${type.toLowerCase()}`).join(',');
};

/**
 * Format file types for display
 * @param {Array<string>} allowedTypes - Array of file extensions
 * @returns {string} Formatted string (e.g., "PDF, DOC, DOCX")
 */
export const formatFileTypes = (allowedTypes = []) => {
    return allowedTypes.map(type => type.toUpperCase()).join(', ');
};

/**
 * Format file size in bytes to compact human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "1KB", "2MB")
 */
export const formatFileSizeCompact = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    const BYTES_PER_UNIT = 1024;
    const SIZE_UNITS = ['B', 'KB', 'MB', 'GB'];
    let unitIndex = 0;
    let size = bytes;
    while (size >= BYTES_PER_UNIT && unitIndex < SIZE_UNITS.length - 1) {
        size /= BYTES_PER_UNIT;
        unitIndex++;
    }
    return `${Math.round(size)}${SIZE_UNITS[unitIndex]}`;
};

/**
 * Format upload date to locale string
 * @param {string} uploadedAt - ISO date string
 * @param {string} locale - Locale code (e.g., 'en-US')
 * @returns {string} Formatted date string
 */
export const formatUploadDate = (uploadedAt, locale) => {
    if (!uploadedAt) return '-';
    return new Date(uploadedAt).toLocaleDateString(locale, { dateStyle: 'medium' });
};

/**
 * Calculate aggregate status for a group of files based on question-level status
 * Uses centralized documentReviewStatusConfig for consistent status definitions
 * @param {Array} files - Array of file objects
 * @param {Object} intl - React Intl instance
 * @param {string} documentReviewStatus - Question-level document review status
 * @returns {Object} Status object with text and type properties
 */
export const getGroupStatus = (files, intl, documentReviewStatus) => {
    // Use documentReviewStatus if provided, otherwise default to notReceived
    const status = documentReviewStatus || 'notReceived';
    const statusConfig = getDocumentReviewStatusConfig(status);
    
    return {
        text: intl.formatMessage({ id: statusConfig.labelId }),
        type: statusConfig.type
    };
};

/**
 * Get the latest upload date from a group of files
 * @param {Array} files - Array of file objects
 * @param {string} locale - Locale code (e.g., 'en-US')
 * @returns {string} Formatted latest date or '-'
 */
export const getLatestUploadDate = (files, locale) => {
    if (!files || files.length === 0) return '-';
    
    const dates = files
        .map(file => file.uploadedAt ? new Date(file.uploadedAt) : null)
        .filter(Boolean);
    
    if (dates.length === 0) return '-';
    
    const latest = new Date(Math.max(...dates));
    return latest.toLocaleDateString(locale, { dateStyle: 'medium' });
};

export const getLatestReviewerComment = (comments) => {
    if (!Array.isArray(comments) || comments.length === 0) {
        return null;
    }
    
    // Filter only reviewer comments (not applicant comments)
    const reviewerComments = comments.filter(c => !c.isApplicantComment);
    
    if (reviewerComments.length === 0) {
        return null;
    }
    
    // Sort by createdAt descending and return the most recent
    const sorted = [...reviewerComments].sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });
    
    return sorted[0] || null;
};

export const getLatestApplicantComment = (comments) => {
    if (!Array.isArray(comments) || comments.length === 0) {
        return null;
    }
    
    // Filter only applicant comments
    const applicantComments = comments.filter(c => c.isApplicantComment === true);
    
    if (applicantComments.length === 0) {
        return null;
    }
    
    // Sort by createdAt descending and return the most recent
    const sorted = [...applicantComments].sort((a, b) => {
        const dateA = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return dateB - dateA;
    });
    
    return sorted[0] || null;
};

export const shouldShowReviewerMessage = (documentReviewStatus, comments) => {
    if (documentReviewStatus !== 'clarificationNeeded' || !Array.isArray(comments)) {
        return false;
    }
    // Check if there's at least one reviewer comment
    return comments.some(c => !c.isApplicantComment);
};

export const shouldShowApplicantMessage = (documentReviewStatus, comments) => {
    if (documentReviewStatus !== 'clarificationNeeded' || !Array.isArray(comments)) {
        return false;
    }
    // Check if there's at least one applicant comment
    return comments.some(c => c.isApplicantComment === true);
};